import { ACTION_SET_KEY_VALUE_TYPE } from "../blueprint/types.js";
import type { BehaviorGraph } from "./types.js";

// Only declarative patches. Host commands, variables, animations and waits keep
// their original event semantics. Custom inputs/buttons are deliberately excluded.
const visualKeys = new Set([
  ...Object.keys(ACTION_SET_KEY_VALUE_TYPE).filter(
    (key) => !key.startsWith("custom"),
  ),
  "location",
  "transform",
  "setListSource",
  "setFilter",
  "setSort",
]);

/** Collapse equivalent automatic visual flows to one live onLoad binding.
 * A graph must contain no manual events, and every event must lead to the same
 * presentation. Pure onChange graphs without initial evaluation are preserved.
 */
export function optimizeRenderBindings(graph: BehaviorGraph): BehaviorGraph {
  const events = graph.nodes.filter((node) => node.type === "event");
  if (
    !events.length ||
    events.some(
      (node) =>
        node.type !== "event" || !["onLoad", "onChange"].includes(node.kind),
    )
  )
    return graph;
  if (
    !events.some(
      (node) =>
        node.type === "event" && (node.kind === "onLoad" || node.triggerOnInit),
    )
  )
    return graph;
  const nodes = new Map(graph.nodes.map((node) => [node.id, node]));
  const outgoing = new Map<string, typeof graph.connections>();
  for (const edge of graph.connections) {
    if (
      nodes
        .get(edge.fromNodeId)
        ?.ports.find((port) => port.id === edge.fromPortId)?.valueType !==
      "flow"
    )
      continue;
    outgoing.set(edge.fromNodeId, [
      ...(outgoing.get(edge.fromNodeId) ?? []),
      edge,
    ]);
  }
  const touched = new Set<string>();
  const fingerprint = (id: string, visiting: Set<string>): string | null => {
    if (visiting.has(id)) return null;
    const node = nodes.get(id);
    if (
      !node ||
      node.type !== "action" ||
      node.kind !== "set" ||
      !visualKeys.has(node.key)
    )
      return null;
    // Explicit targets can patch other elements, whose event order may matter.
    if (
      graph.connections.some(
        (edge) => edge.toNodeId === id && edge.toPortId === "target",
      )
    )
      return null;
    touched.add(id);
    const children = (outgoing.get(id) ?? []).map((edge) =>
      fingerprint(edge.toNodeId, new Set(visiting).add(id)),
    );
    if (children.some((value) => value === null)) return null;
    const inputs = node.ports
      .filter((port) => port.direction === "input" && port.valueType !== "flow")
      .map((port) => {
        const wire = graph.connections.find(
          (edge) => edge.toNodeId === id && edge.toPortId === port.id,
        );
        return [
          port.id,
          port.valueType,
          wire ? [wire.fromNodeId, wire.fromPortId] : port.value,
        ];
      });
    return JSON.stringify([node.key, inputs, children]);
  };
  const signatures = events.map((event) =>
    (outgoing.get(event.id) ?? []).map((edge) =>
      fingerprint(edge.toNodeId, new Set()),
    ),
  );
  if (
    signatures.some(
      (values) => !values.length || values.some((value) => value === null),
    )
  )
    return graph;
  if (new Set(signatures.map((values) => JSON.stringify(values))).size !== 1)
    return graph;
  const survivor =
    events.find((node) => node.type === "event" && node.kind === "onLoad") ??
    events[0];
  if (
    events.length === 1 &&
    survivor.type === "event" &&
    survivor.kind === "onLoad"
  )
    return graph;
  const keepFlow = new Set<string>();
  const collect = (id: string) => {
    if (keepFlow.has(id)) return;
    keepFlow.add(id);
    for (const edge of outgoing.get(id) ?? []) collect(edge.toNodeId);
  };
  collect(survivor.id);
  const removed = new Set([
    ...events
      .filter((event) => event.id !== survivor.id)
      .map((event) => event.id),
    ...Array.from(touched).filter((id) => !keepFlow.has(id)),
  ]);
  return {
    ...graph,
    nodes: graph.nodes
      .filter((node) => !removed.has(node.id))
      .map((node) =>
        node.id === survivor.id && node.type === "event"
          ? {
              ...node,
              kind: "onLoad",
              objectSource: undefined,
              objectFieldPath: undefined,
              triggerOnInit: undefined,
            }
          : node,
      ),
    connections: graph.connections.filter(
      (edge) => !removed.has(edge.fromNodeId) && !removed.has(edge.toNodeId),
    ),
  };
}
